import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  Pagination,
} from '@mui/material';
import {
  MenuBook as CourseIcon,
  OndemandVideo as VideoIcon,
} from '@mui/icons-material';
import SearchFilters from '../../components/Search/SearchFilters';
import courseService from '../../services/course.service';
import { Course } from '../../types';
import CourseDetailsModal from '../../components/CourseDetailsModal';

const ITEMS_PER_PAGE = 30;

const SearchCourses: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(1);

  // Calculate paginated courses
  const totalPages = Math.ceil(filteredCourses.length / ITEMS_PER_PAGE);
  const paginatedCourses = filteredCourses.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedTags]);

  const handleCourseClick = (course: Course) => {
    setSelectedCourse(course);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCourse(null);
  };

  // Load public courses
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await courseService.getAvailableCourses();
        setCourses(data);

        // Extract unique tags
        const tags = new Set<string>();
        data.forEach((course: Course) => {
          course.tags?.forEach((tag: string) => tags.add(tag));
        });
        setAvailableTags(Array.from(tags).sort());
      } catch (err) {
        console.error('Error loading courses:', err);
        setError('Erreur lors du chargement des cours');
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  // Filter courses
  useEffect(() => {
    let filtered = [...courses];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (course) =>
          course.title?.toLowerCase().includes(query) ||
          course.latex_content?.toLowerCase().includes(query)
      );
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter((course) =>
        selectedTags.every((tag) => course.tags?.includes(tag))
      );
    }

    setFilteredCourses(filtered);
  }, [searchQuery, selectedTags, courses]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Search Filters */}
        <Grid item xs={12} md={3}>
          <SearchFilters
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            availableTags={availableTags}
            contentType="cours"
          />
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={9}>
          <Box>
            {/* Results count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredCourses.length} cours trouvé{filteredCourses.length > 1 ? 's' : ''}
            </Typography>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}

            {/* Results */}
            <Grid container spacing={2}>
              {paginatedCourses.map((course) => (
                <Grid item xs={12} sm={6} md={4} key={course.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => handleCourseClick(course)}
                  >
                    {course.thumbnail_url && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={course.thumbnail_url}
                        alt={course.title}
                        sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {course.title}
                      </Typography>

                      {course.created_by_name && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Par {course.created_by_name}
                        </Typography>
                      )}

                      {/* Quick Recap Tags */}
                      {(course.quick_recap_course_title || course.quick_recap_video_title) && (
                        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }} flexWrap="wrap" gap={0.5}>
                          {course.quick_recap_course_title && (
                            <Chip
                              icon={<CourseIcon />}
                              label="Quick Recap Cours"
                              size="small"
                              color="primary"
                              variant="filled"
                            />
                          )}
                          {course.quick_recap_video_title && (
                            <Chip
                              icon={<VideoIcon />}
                              label="Quick Recap Vidéo"
                              size="small"
                              color="error"
                              variant="filled"
                            />
                          )}
                        </Stack>
                      )}

                      {course.tags && course.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {course.tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredCourses.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Aucun cours ne correspond à vos critères de recherche
                </Typography>
              </Box>
            )}

            {/* Bottom Pagination */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Course Details Modal */}
      <CourseDetailsModal
        course={selectedCourse}
        open={modalOpen}
        onClose={handleCloseModal}
      />
    </Box>
  );
};

export default SearchCourses;
